import React from 'react';
import { Box, Paper, Skeleton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Tooltip, Alert, Grid } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import InboxIcon from '@mui/icons-material/Inbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TodayIcon from '@mui/icons-material/Today';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import FunctionsIcon from '@mui/icons-material/Functions';

const LoadingSkeleton = () => (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
            {[...Array(8)].map((_, i) => (
                <Grid item xs={6} sm={4} md={3} key={i} sx={{ minWidth: '150px' }}>
                    <Paper sx={{ p: 2, background: 'rgba(0,0,0,0.25)', borderRadius: '12px', minHeight: '150px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'grey.700' }} />
                        <Skeleton variant="text" width="80%" sx={{ mt: 1.5, bgcolor: 'grey.700', fontSize: '0.9rem' }} />
                        <Skeleton variant="text" width="60%" sx={{ bgcolor: 'grey.700', fontSize: '1.5rem', height: 36 }} />
                    </Paper>
                </Grid>
            ))}
        </Grid>
        <Skeleton variant="text" width="40%" sx={{ mb: 2, bgcolor: 'grey.800', fontSize: '1.2rem' }} />
        {[...Array(4)].map((_, i) => ( <Skeleton key={i} variant="rectangular" width="100%" height={50} sx={{ bgcolor: 'grey.800', borderRadius: '8px', mb: 1.5 }} /> ))}
    </Box>
);

const SummaryCard = ({ icon, title, value }) => (
    <Paper
        component={motion.div}
        whileHover={{ scale: 1.05, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)" }}
        transition={{ type: "spring", stiffness: 300 }}
        sx={{
            p: 2,
            background: 'rgba(0,0,0,0.25)',
            textAlign: 'center',
            borderRadius: '12px',
            minHeight: '150px',
            minWidth: '150px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
        }}
    >
        {icon}
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1.5 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{value}</Typography>
    </Paper>
);

const OverviewDetailsTable = ({ loading, data, error, statInfo, onClose }) => {
    const containerVariants = { hidden: { opacity: 0, y: -20, height: 0 }, visible: { opacity: 1, y: 0, height: 'auto', transition: { type: 'spring', damping: 30, stiffness: 200 } }, exit: { opacity: 0, y: -20, height: 0, transition: { duration: 0.3 } } };
    const getStatusChip = (status) => {
        const statusMap = { 'تکمیل شده': { icon: <CheckCircleIcon fontSize="small"/>, color: 'success' }, 'در حال انجام': { icon: <LocalShippingIcon fontSize="small"/>, color: 'info' }, 'در انتظار بررسی': { icon: <PendingIcon fontSize="small"/>, color: 'warning' }, 'ناموفق': { icon: <ErrorIcon fontSize="small"/>, color: 'error' }, };
        const chipProps = statusMap[status] || { label: status, color: 'default' };
        return <Chip size="small" {...chipProps} label={status} />;
    };

    const renderContent = () => {
        if (loading) return <LoadingSkeleton />;
        if (error) return <Alert severity="error" sx={{ m: 3, bgcolor: 'rgba(255,0,0,0.3)', color: '#fff' }}>{error}</Alert>;
        if (data?.table_data) {
            if (data.table_data.length === 0) {
                return ( <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}> <InboxIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }}/> <Typography>هیچ سفارشی برای نمایش در این بازه زمانی یافت نشد.</Typography> </Box> );
            }
            const { table_data, summary_stats } = data;
            const totalAmount = table_data.reduce((sum, order) => sum + order.total, 0);
            const formatNumber = (num) => new Intl.NumberFormat('fa-IR').format(num);
            
            const summaryCards = [
                { icon: <ShoppingBasketIcon sx={{ color: '#3498db', fontSize: 32 }}/>, title: "تعداد کل سفارشات", value: formatNumber(table_data.length) },
                { icon: <PointOfSaleIcon sx={{ color: '#2ecc71', fontSize: 32 }}/>, title: "مجموع فروش (ریال)", value: formatNumber(totalAmount) },
                { icon: <Inventory2Icon sx={{ color: '#9b59b6', fontSize: 32 }}/>, title: "مجموع محصولات فروش‌رفته", value: formatNumber(summary_stats.total_items_sold) },
                { icon: <FunctionsIcon sx={{ color: '#e67e22', fontSize: 32 }}/>, title: "میانگین مبلغ سفارش", value: formatNumber(summary_stats.avg_order_value) },
                { icon: <TodayIcon sx={{ color: '#f1c40f', fontSize: 32 }}/>, title: "روز اوج فروش (مبلغ)", value: summary_stats.peak_day_by_value || '-' },
                { icon: <TrendingUpIcon sx={{ color: '#e74c3c', fontSize: 32 }}/>, title: "روز اوج فروش (تعداد)", value: summary_stats.peak_day_by_count || '-' },
                { icon: <AccessTimeIcon sx={{ color: '#1abc9c', fontSize: 32 }}/>, title: "ساعت اوج ترافیک", value: summary_stats.busiest_hour || '-' },
                { icon: <StarIcon sx={{ color: '#f39c12', fontSize: 32 }}/>, title: "برترین مشتری", value: summary_stats.top_customer || '-' },
            ];
            return (
                 <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <Grid container spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
                        {summaryCards.map(card => ( <Grid item xs={6} sm={4} md={3} key={card.title} sx={{ minWidth: '150px' }}> <SummaryCard {...card} /> </Grid> ))}
                    </Grid>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>لیست سفارشات</Typography>
                    <TableContainer component={Paper} sx={{ background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(5px)', borderRadius: '12px', maxHeight: 500 }}>
                         <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={{ '& .MuiTableCell-root': { background: 'rgba(0,0,0,0.5)', color: '#fff', fontWeight: 'bold', borderBottom: '2px solid #3498db' } }}>
                                    <TableCell>سفارش</TableCell>
                                    <TableCell>مشتری</TableCell>
                                    <TableCell align="center">تاریخ</TableCell>
                                    <TableCell align="center">وضعیت</TableCell>
                                    <TableCell align="center">تعداد محصولات</TableCell>
                                    <TableCell align="right">مبلغ کل (ریال)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {table_data.map((row) => (
                                    <TableRow key={row.id} sx={{ '&:hover': { background: 'rgba(255,255,255,0.05)' }, '&:last-child td, &:last-child th': { border: 0 }, '& .MuiTableCell-root': { color: 'rgba(255,255,255,0.85)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}}>
                                        <TableCell><Tooltip title="مشاهده سفارش در ووکامرس" placement="top"><Typography component="a" href={row.link} target="_blank" sx={{color: '#64b5f6', textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>#{row.id}</Typography></Tooltip></TableCell>
                                        <TableCell>{row.customer_name}</TableCell>
                                        <TableCell align="center">{row.date}</TableCell>
                                        <TableCell align="center">{getStatusChip(row.status)}</TableCell>
                                        <TableCell align="center">{row.item_count}</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold', direction: 'ltr'}}>{new Intl.NumberFormat('fa-IR').format(row.total)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        }
        return null;
    }
    return (
        <AnimatePresence>
            <Box component={motion.div} variants={containerVariants} initial="hidden" animate="visible" exit="exit" sx={{ mt: 4, background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8), rgba(10, 25, 41, 0.9))', backdropFilter: 'blur(12px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '16px', mb: 3, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>تحلیل دقیق: {statInfo?.title}</Typography>
                    <Tooltip title="بستن"><IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon /></IconButton></Tooltip>
                </Box>
                {renderContent()}
            </Box>
        </AnimatePresence>
    );
};
export default OverviewDetailsTable;